import type { Constructor, ExecOf, OperationClass } from './types';

/**
 * Minimal IoC container for dependency injection without decorators or reflect-metadata.
 * Uses class constructors as tokens and lazy singleton resolution. Compatible with
 * Vite/esbuild (no decorator support required).
 */
export class Container {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Maps are keyed by constructor token and hold heterogeneous instances; any is the only viable value type here
    private instances = new Map<Constructor, any>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- factory return types are heterogeneous; resolved to precise types via the generic resolve() overloads
    private factories = new Map<Constructor, () => any>();

    /**
     * Register a custom factory for a token. Resolved lazily and cached as a
     * singleton on first `resolve()`, identical to {@link register} — but the
     * factory itself decides how the instance is constructed.
     *
     * Use this when {@link register}'s "constructor + dep tokens" shape doesn't
     * fit, e.g. when the service takes a collection of dependencies, when
     * construction involves non-service inputs (arrays, config objects), or when
     * the dependency graph is assembled from internal types that aren't worth
     * registering as their own tokens.
     *
     * The factory receives no arguments; resolve dependencies inside it via
     * `container.resolve(...)`. Those resolves happen at first use, so bootstrap
     * order between registration calls doesn't matter — only the order between
     * `resolve()` calls does.
     *
     * @template TService - the class type being registered
     * @param token - the class constructor used as the registration key
     * @param factory - zero-arg function returning the instance to cache
     * @returns this container, for chaining
     *
     * @example
     * container.factory(AnalyticsTrackingService, () => {
     *     const config = container.resolve(ConfigService);
     *     return new AnalyticsTrackingService([new Ga4Destination(new GtmTransport(config))]);
     * });
     */
    factory<TService>(token: Constructor<TService>, factory: () => TService): this {
        this.factories.set(token, factory);
        return this;
    }

    /**
     * Register a pre-built instance (services, HttpClient, etc.).
     *
     * @template TService - the class type being registered
     * @param token - the class constructor used as the registration key
     * @param value - the pre-built instance to store
     * @returns this container, for chaining
     */
    instance<TService>(token: Constructor<TService>, value: TService): this {
        this.instances.set(token, value);
        return this;
    }

    /**
     * Register a class with its dependency tokens. Resolved lazily, cached as singleton.
     *
     * @template TService - the class type being registered
     * @param token - the class constructor used as the registration key
     * @param deps - ordered list of dependency class constructors, matching the constructor signature
     * @returns this container, for chaining
     *
     * @example
     * container.register(UploadAsset, [TechlineService, AnalyticsService]);
     */
    register<TService>(token: Constructor<TService>, deps: Constructor[]): this {
        this.factories.set(token, () => {
            const resolved = deps.map((dep) => this.resolve(dep));
            return new token(...resolved);
        });
        return this;
    }

    /**
     * Resolve an instance. Singleton; created once on first resolve, cached forever.
     *
     * @template TService - the class type to resolve
     * @param token - the class constructor to look up
     * @throws Error when nothing is registered for the given token
     * @returns the singleton instance
     */
    resolve<TService>(token: Constructor<TService>): TService {
        if (this.instances.has(token)) {
            return this.instances.get(token);
        }

        const factory = this.factories.get(token);
        if (!factory) {
            throw new Error(`Nothing registered for ${token.name}`);
        }

        const instance = factory();
        this.instances.set(token, instance);
        return instance;
    }

    /**
     * Resolve multiple services at once. Returns a tuple whose entries match the
     * order and types of the given tokens, making multi-service resolution a single
     * destructurable call.
     *
     * @template TServices - tuple of constructor types to resolve
     * @param tokens - class constructors to look up
     * @throws Error when nothing is registered for any of the given tokens
     * @returns a tuple of singleton instances matching the input order
     *
     * @example
     * const [techline, analytics] = container.resolveAll(TechlineService, AnalyticsService);
     */
    resolveAll<TServices extends Constructor[]>(
        ...tokens: [...TServices]
    ): { [K in keyof TServices]: InstanceType<TServices[K]> } {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- tuple type cannot be derived from a dynamic map(); the public overload signature provides the precise type
        return tokens.map((token) => this.resolve(token)) as any;
    }

    /**
     * Resolve a single operation class and return its bound `exec` function so
     * callers can invoke it as a plain function without needing `.exec()`.
     *
     * @template TOperationClass - the operation constructor to resolve
     * @param token - the operation class to look up
     * @throws Error when nothing is registered for the given token
     * @returns the bound `exec` function of the resolved operation
     *
     * @example
     * const fetchAssets = container.resolveOperation(FetchAssets);
     * const assets = await fetchAssets(caseNumber);
     */
    resolveOperation<TOperationClass extends OperationClass>(token: TOperationClass): ExecOf<TOperationClass> {
        const instance = this.resolve(token);
        return instance.exec.bind(instance) as ExecOf<TOperationClass>;
    }

    /**
     * Resolve multiple operation classes and return their bound `exec` functions.
     * Combines the convenience of {@link resolveOperation} with variadic tuple typing
     * so callers can destructure several operations in one call.
     *
     * @template TOperations - tuple of operation constructors to resolve
     * @param tokens - operation classes to look up in the container
     * @throws Error when nothing is registered for any of the given tokens
     * @returns a tuple of bound `exec` functions matching the input order
     *
     * @example
     * const [fetchAssets, deleteAsset] = container.resolveOperations(FetchAssets, DeleteAsset);
     * const assets = await fetchAssets(caseNumber);
     * await deleteAsset(assetId);
     */
    resolveOperations<TOperations extends OperationClass[]>(
        ...tokens: [...TOperations]
    ): { [K in keyof TOperations]: ExecOf<TOperations[K]> } {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- tuple type cannot be derived from a dynamic map(); the public overload signature provides the precise type
        return tokens.map((token) => this.resolveOperation(token)) as any;
    }
}
